import { useTranslation } from "react-i18next";

import type { TextComponentProps } from "../types";

/**
 * TextComponent - renders plain text.
 * Supports static text from props or dynamic interpolation from data.text.
 */
function TextComponent({ text, dataKey, data, as = "span", variant = "default" }: TextComponentProps) {
  const { t } = useTranslation();

  const resolved = dataKey && data ? data[dataKey] : undefined;

  const textValue =
    resolved != null
      ? String(resolved)
      : text != null && String(text).trim() !== ""
        ? text
        : data != null && "text" in data
          ? String(data.text ?? "")
          : "";

  // Handle multiple translation keys separated by "|" for multi-line messages
  const translationKeys = textValue.split("|").map((key) => key.trim()).filter(Boolean);
  
  if (translationKeys.length > 1) {
    // Multiple keys - render as separate lines
    return (
      <div className="flex flex-col gap-1">
        {translationKeys.map((key, index) => (
          <span key={index}>{t(key)}</span>
        ))}
      </div>
    );
  }

  // Single key or empty - render normally
  const displayText = textValue ? t(textValue) : "";
  
  // Get heading classes based on the 'as' prop
  const headingClasses: Record<string, string> = {
    h1: "text-2xl font-bold",
    h2: "text-xl font-semibold",
    h3: "text-lg font-semibold",
    h4: "text-base font-semibold",
    h5: "text-sm font-semibold",
    h6: "text-xs font-semibold",
  };
  
  if (as !== "span") {
    const HeadingTag = as;
    return <HeadingTag className={headingClasses[as]}>{displayText}</HeadingTag>;
  }
  
  if (variant === "subtext" || variant === "description") {
    return <p className="text-sm text-muted-foreground">{displayText}</p>;
  }
  
  return <span>{displayText}</span>;
}

export { TextComponent };
